'use client';

import { useEffect, useMemo, useState, useRef } from 'react';
import dynamic from 'next/dynamic';
import { TrendingUp, PieChart, BarChart3, Calendar, ChevronDown, ChevronUp, Calculator, Info } from 'lucide-react';
import Link from 'next/link';
import { formatCurrency } from '@/utils/format';

const MonthlySpendingChart = dynamic(
  () => import('@/components/MonthlySpendingChart').then(mod => ({ default: mod.MonthlySpendingChart })),
  {
    loading: () => (
      <div className="h-[400px] flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading chart...</p>
      </div>
    ),
    ssr: false
  }
);
import { CategoryBreakdown } from '@/components/CategoryBreakdown';
import { MonthlyComparison } from '@/components/MonthlyComparison';
import { CardSpendingComparison } from '@/components/CardSpendingComparison';
import { APRCalculator } from '@/components/APRCalculator';
import { TransactionCatalogModal, type TransactionCatalogContext } from '@/components/TransactionCatalogModal';
import { getCustomTimeframe } from '@/utils/timeframe';
import { calculateComparisonRanges } from '@/utils/comparisonRanges';

interface AnalyticsContentProps {
  isLoggedIn: boolean;
  hasSubscription: boolean;
}

type SpendingCardSelection = {
  cardId?: string;
  name: string;
  mask?: string;
  amount: number;
};

export function AnalyticsContent({ isLoggedIn, hasSubscription }: AnalyticsContentProps) {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [isAPRCalculatorOpen, setIsAPRCalculatorOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [showCardComparison, setShowCardComparison] = useState(false);
  const [customStartDay, setCustomStartDay] = useState<number>(1);
  const [chartSelectedYear, setChartSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [useEqualDayComparison, setUseEqualDayComparison] = useState(false);
  const cardSpendingSectionRef = useRef<HTMLDivElement>(null);
  const [transactionModalContext, setTransactionModalContext] = useState<TransactionCatalogContext | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState(() => Date.now());
  const [error, setError] = useState<string | null>(null);

  // Load persisted values from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedMonth = localStorage.getItem('analytics-selected-month');
      const savedStartDay = localStorage.getItem('analytics-custom-start-day');

      console.log('🔄 Loading persisted analytics values:', {
        savedMonth,
        savedStartDay,
        currentSelectedMonth: selectedMonth,
        currentCustomStartDay: customStartDay
      });

      if (savedMonth) {
        setSelectedMonth(savedMonth);
      }

      if (savedStartDay) {
        const startDayNum = parseInt(savedStartDay, 10);
        if (!isNaN(startDayNum) && startDayNum >= 1 && startDayNum <= 31) {
          console.log('🔄 Setting customStartDay from localStorage:', startDayNum);
          setCustomStartDay(startDayNum);
        }
      }
    }
  }, []);

  // Persist selectedMonth to localStorage when it changes
  useEffect(() => {
    if (typeof window !== 'undefined' && selectedMonth) {
      console.log('💾 Saving selectedMonth to localStorage:', selectedMonth);
      localStorage.setItem('analytics-selected-month', selectedMonth);
    }
  }, [selectedMonth]);

  // Persist customStartDay to localStorage when it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      console.log('💾 Saving customStartDay to localStorage:', customStartDay);
      localStorage.setItem('analytics-custom-start-day', customStartDay.toString());
    }
  }, [customStartDay]);

  const fallbackMonth = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const monthOptions = useMemo(() => {
    if (!isLoggedIn) {
      return Array.from({ length: 6 }, (_, index) => {
        const optionDate = new Date();
        optionDate.setMonth(optionDate.getMonth() - index);
        return `${optionDate.getFullYear()}-${String(optionDate.getMonth() + 1).padStart(2, '0')}`;
      });
    }

    const options = new Set<string>();
    availableMonths.forEach((month) => {
      if (month) {
        options.add(month);
      }
    });

    if (selectedMonth) {
      options.add(selectedMonth);
    }

    if (!options.size) {
      options.add(fallbackMonth);
    }

    return Array.from(options);
  }, [isLoggedIn, availableMonths, selectedMonth, fallbackMonth]);

  const monthSelectValue = useMemo(() => {
    if (selectedMonth && monthOptions.includes(selectedMonth)) {
      return selectedMonth;
    }

    return monthOptions[0] || fallbackMonth;
  }, [selectedMonth, monthOptions, fallbackMonth]);

  const formatRangeLabel = (start: Date, end: Date): string => {
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 'Custom range';
    }
    const sameYear = start.getFullYear() === end.getFullYear();
    const startLabel = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' })
    }).format(start);
    const endLabel = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(end);
    return `${startLabel} – ${endLabel}`;
  };

  const activeYearMonth = useMemo(() => {
    if (selectedMonth) {
      const [yearStr, monthStr] = selectedMonth.split('-');
      const year = Number(yearStr);
      const month = Number(monthStr);
      if (!Number.isNaN(year) && !Number.isNaN(month)) {
        return { year, month };
      }
    }
    if (availableMonths.length > 0) {
      const [yearStr, monthStr] = availableMonths[0].split('-');
      const year = Number(yearStr);
      const month = Number(monthStr);
      if (!Number.isNaN(year) && !Number.isNaN(month)) {
        return { year, month };
      }
    }
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  }, [selectedMonth, availableMonths]);

  const handleCardSpendingSelect = (card: SpendingCardSelection, period?: 'current' | 'previous') => {
    if (!card || !isLoggedIn || !hasSubscription) return;

    let dateRange;
    let label;
    if (period === 'current') {
      dateRange = {
        startISO: comparisonRanges.current.start.toISOString(),
        endISO: comparisonRanges.current.end.toISOString()
      };
      label = formatRangeLabel(comparisonRanges.current.start, comparisonRanges.current.end);
    } else if (period === 'previous') {
      dateRange = {
        startISO: comparisonRanges.previous.start.toISOString(),
        endISO: comparisonRanges.previous.end.toISOString()
      };
      label = formatRangeLabel(comparisonRanges.previous.start, comparisonRanges.previous.end);
    } else {
      // Default: use current period for backward compatibility
      dateRange = {
        startISO: comparisonRanges.current.start.toISOString(),
        endISO: comparisonRanges.current.end.toISOString()
      };
      label = formatRangeLabel(comparisonRanges.current.start, comparisonRanges.current.end);
    }

    setTransactionModalContext({
      origin: 'analytics-card',
      cardId: card.cardId,
      cardName: card.name,
      cardMask: card.mask,
      dateRange: {
        ...dateRange,
        label
      },
      meta: {
        totalSpend: typeof card.amount === 'number' ? card.amount : undefined
      }
    });
  };

  // Handle total spending trend clicks (NEW FUNCTION)
  const handleTotalClick = (period: 'current' | 'previous') => {
    if (!isLoggedIn || !hasSubscription) return;
    let dateRange;
    let label;
    let totalAmount;

    if (period === 'current') {
      // Use current period range
      dateRange = {
        startISO: comparisonRanges.current.start.toISOString(),
        endISO: comparisonRanges.current.end.toISOString()
      };
      label = formatRangeLabel(comparisonRanges.current.start, comparisonRanges.current.end);
      totalAmount = displayData?.totalSpendThisMonth || 0;
    } else {
      // For previous period, we need to match the API's calculation logic
      // If useEqualDayComparison is true, use equal-day range, otherwise use full previous month
      const { year, month } = activeYearMonth;
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const prevTimeframe = getCustomTimeframe(prevYear, prevMonth, customStartDay || 1);

      let previousStart: Date;
      let previousEnd: Date;

      if (useEqualDayComparison && isPartialMonthScenario) {
        // Match API's equal-day calculation
        const currentStart = getCustomTimeframe(year, month, customStartDay || 1).start;
        const currentEnd = isPartialMonthScenario ? new Date() : getCustomTimeframe(year, month, customStartDay || 1).end;
        const daysSoFar = Math.floor((currentEnd.getTime() - currentStart.getTime()) / (24 * 60 * 60 * 1000)) + 1;

        previousStart = prevTimeframe.start;
        previousEnd = new Date(previousStart.getTime() + (daysSoFar - 1) * 24 * 60 * 60 * 1000);
        totalAmount = displayData?.totalSpendLastMonthEqualDays || 0;
      } else {
        // Use full previous month
        previousStart = prevTimeframe.start;
        previousEnd = prevTimeframe.end;
        totalAmount = displayData?.totalSpendLastMonth || 0;
      }

      dateRange = {
        startISO: previousStart.toISOString(),
        endISO: previousEnd.toISOString()
      };
      label = formatRangeLabel(previousStart, previousEnd);
    }

    console.log('🎯 Total Click Handler:', {
      period,
      useEqualDayComparison,
      isPartialMonthScenario,
      dateRange,
      label,
      totalAmount
    });

    setTransactionModalContext({
      origin: 'analytics-total',
      cardId: undefined, // All cards
      cardName: 'All Cards',
      cardMask: undefined,
      dateRange: {
        ...dateRange,
        label
      },
      meta: {
        totalSpend: totalAmount,
        includeAllCards: true
      }
    });
  };

  const mockData = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Generate last 4 months dynamically (including current)
    const monthlySpend = [];
    for (let i = 3; i >= 0; i--) {
      const monthDate = new Date(currentYear, currentMonth - i, 1);
      const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      // Generate realistic spending amounts that vary by month
      let amount: number;
      if (i === 0) {
        // Current month - partial spending (early in month)
        amount = 638;
      } else if (i === 1) {
        // Last month - highest spending
        amount = 1958;
      } else if (i === 2) {
        // Two months ago - peak spending
        amount = 1994;
      } else {
        // Three months ago - moderate spending
        amount = 1822;
      }

      monthlySpend.push({ month: monthLabel, amount });
    }

    const base = {
    totalSpendThisMonth: 637.50, // Sum of current open cycles: 376.50 + 119.50 + 141.50
    totalSpendLastMonth: 1147.80, // Sum of last month amounts: 892.30 + 156.80 + 98.70
    totalSpendLastMonthEqualDays: 730.20, // Equal days comparison (roughly 19/31 of full month)
    monthlySpend,
    categories: [
      { name: 'Dining', amount: 259, percentage: 40.6 },
      { name: 'Shopping', amount: 160, percentage: 25.1 },
      { name: 'Groceries', amount: 118, percentage: 18.5 },
      { name: 'Gas', amount: 70, percentage: 11.0 },
      { name: 'Travel', amount: 28, percentage: 4.4 },
      { name: 'Other', amount: 2.50, percentage: 0.4 },
    ],
    cardSpending: [
      {
        name: 'Chase Sapphire Preferred',
        amount: 376.50,
        color: 'bg-blue-500',
        lastMonthAmount: 892.30,
        lastMonthAmountEqualDays: 568.50, // Equal days comparison (roughly 19/31 of full month)
        change: -57.8 // Significant decrease
      },
      {
        name: 'Capital One Venture',
        amount: 119.50,
        color: 'bg-green-500',
        lastMonthAmount: 156.80,
        lastMonthAmountEqualDays: 99.90, // Equal days comparison
        change: -23.8 // Moderate decrease
      },
      {
        name: 'American Express Gold',
        amount: 141.50,
        color: 'bg-orange-500',
        lastMonthAmount: 98.70,
        lastMonthAmountEqualDays: 62.80, // Equal days comparison
        change: 43.4 // Notable increase
      },
    ],
    monthlyComparison: [
      { category: 'Dining', thisMonth: 259, lastMonth: 686, change: -62.2 }, // Current vs Previous
      { category: 'Shopping', thisMonth: 160, lastMonth: 586, change: -72.7 }, // Current vs Previous
      { category: 'Groceries', thisMonth: 118, lastMonth: 513, change: -77.0 }, // Current vs Previous
      { category: 'Travel', thisMonth: 28, lastMonth: 115, change: -75.5 }, // Current vs Previous
      { category: 'Gas', thisMonth: 70, lastMonth: 198, change: -64.6 }, // Current vs Previous
    ],
    transactionCount: 27, // Sum of transaction counts from current open cycles (12 + 7 + 8)
    };

    const sortedMonthlySpend = [...base.monthlySpend].sort((a, b) => b.amount - a.amount);
    return { ...base, monthlySpend: sortedMonthlySpend };
  }, []);

  const fetchAnalytics = async (month?: string, startDay?: number) => {
    if (!isLoggedIn || !hasSubscription) return;

    const startTime = Date.now();

    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (month) params.append('month', month);
      if (startDay && startDay !== 1) params.append('startDay', startDay.toString());

      const url = params.toString()
        ? `/api/user/analytics?${params.toString()}`
        : '/api/user/analytics';
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Analytics data received:', data);
        setAnalytics(data);
        setLastFetchTime(Date.now());
        setError(null);

        // Update available months and selected month from API response
        if (data.availableMonths) {
          setAvailableMonths(data.availableMonths);
        }
        if (data.selectedMonth) {
          setSelectedMonth(data.selectedMonth);
        }
      } else {
        setError(`Failed to load analytics (${response.status})`);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      // Ensure loading appears for at least 500ms
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 500 - elapsed);
      
      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    }
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    fetchAnalytics(month, customStartDay);
  };

  const getSmartMonthForStartDay = (startDay: number, currentSelectedMonth: string) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // September = 9
    const currentDay = now.getDate(); // 18

    // Parse the currently selected month
    const [selectedYear, selectedMonthNum] = currentSelectedMonth.split('-').map(Number);

    console.log('Smart month logic:', {
      currentYear,
      currentMonth,
      currentDay,
      selectedYear,
      selectedMonthNum,
      startDay,
      currentSelectedMonth
    });

    // ONLY move month back if ALL conditions are true:
    // 1. We are viewing the current calendar year
    // 2. We are viewing the current calendar month
    // 3. The start day is greater than today's date
    const isCurrentYear = selectedYear === currentYear;
    const isCurrentMonth = selectedMonthNum === currentMonth;
    const isStartDayInFuture = startDay > currentDay;

    console.log('Conditions:', {
      isCurrentYear,
      isCurrentMonth,
      isStartDayInFuture,
      shouldMoveBack: isCurrentYear && isCurrentMonth && isStartDayInFuture
    });

    // If we're viewing ANY historical month (different year OR different month), NEVER change
    if (!isCurrentYear || !isCurrentMonth) {
      console.log('Viewing historical month - keeping current selection');
      return currentSelectedMonth;
    }

    // If we're viewing current month but start day is not in future, stay on current month
    if (!isStartDayInFuture) {
      console.log('Current month but start day not in future - keeping current month');
      return currentSelectedMonth;
    }

    // Only now do we move to previous month (current month + start day > today)
    console.log('Moving to previous month because start day is in future');
    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    return `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
  };

  const handleStartDayChange = (day: number) => {
    setCustomStartDay(day);

    // Keep the selected month constant - don't let start day changes affect month
    const currentMonth = selectedMonth || monthSelectValue;
    fetchAnalytics(currentMonth, day);
  };

  useEffect(() => {
    fetchAnalytics();
  }, [isLoggedIn]);

  // Refresh stale date references when the tab regains focus
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setLastFetchTime(Date.now());
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Toggle is now instant - no API refetch needed since both values are pre-computed

  // Calculate optimal visible months based on available vertical space
  const displayData = (isLoggedIn && hasSubscription) ? (analytics || {
    totalSpendThisMonth: 0,
    monthlySpend: [],
    categories: [],
    cardSpending: [],
    monthlyComparison: [],
    transactionCount: 0,
  }) : mockData;

  // Extract available years from monthly spending data
  const availableChartYears = useMemo(() => {
    if (!displayData.monthlySpend || displayData.monthlySpend.length === 0) {
      return [new Date().getFullYear().toString()];
    }

    const years = new Set<string>();
    displayData.monthlySpend.forEach((item: { month: string }) => {
      const yearMatch = item.month.match(/\d{4}/);
      if (yearMatch) {
        years.add(yearMatch[0]);
      }
    });

    return Array.from(years).sort((a, b) => parseInt(b) - parseInt(a));
  }, [displayData.monthlySpend]);

  // Determine if we're viewing a partial (in-progress) billing cycle (must be before avgPerDay, comparisonRanges)
  const isPartialMonthScenario = useMemo(() => {
    // Prefer API-computed value when available
    if (analytics && typeof analytics.isPartialMonth === 'boolean') {
      return analytics.isPartialMonth;
    }
    // Fallback: compute locally (matches API logic using timeframe bounds)
    const { year, month } = activeYearMonth;
    const today = new Date();
    const timeframe = getCustomTimeframe(year, month, customStartDay || 1);
    const isCurrentCycle = today >= timeframe.start && today <= timeframe.end;
    return isCurrentCycle && today < timeframe.end;
  }, [analytics, activeYearMonth, customStartDay]);

  const avgPerDay = useMemo(() => {
    if (!displayData.totalSpendThisMonth) return 0;
    const { year, month } = activeYearMonth;
    const timeframe = getCustomTimeframe(year, month, customStartDay || 1);
    const today = new Date();
    const cycleEnd = isPartialMonthScenario ? today : timeframe.end;
    const actualDays = Math.floor(
      (cycleEnd.getTime() - timeframe.start.getTime()) / (24 * 60 * 60 * 1000)
    ) + 1;
    return displayData.totalSpendThisMonth / Math.max(actualDays, 1);
  }, [displayData.totalSpendThisMonth, customStartDay, isPartialMonthScenario, activeYearMonth]);

  const topCategory = displayData.categories?.[0]?.name || (isLoggedIn ? '' : 'Dining');

  // Calculate comparison date ranges using inline logic to match API
  const comparisonRanges = useMemo(() => {
    const { year, month } = activeYearMonth;
    const today = new Date();
    const currentTimeframe = getCustomTimeframe(year, month, customStartDay || 1);

    // Current period range (always from cycle start to today if partial)
    const currentStart = currentTimeframe.start;
    const currentEnd = isPartialMonthScenario ? today : currentTimeframe.end;

    // Previous period range
    let previousStart: Date;
    let previousEnd: Date;

    if (useEqualDayComparison && isPartialMonthScenario) {
      // Equal day comparison: same number of days in previous cycle
      const daysSoFar = Math.floor((currentEnd.getTime() - currentStart.getTime()) / (24 * 60 * 60 * 1000)) + 1;
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const prevTimeframe = getCustomTimeframe(prevYear, prevMonth, customStartDay || 1);

      previousStart = prevTimeframe.start;
      previousEnd = new Date(previousStart.getTime() + (daysSoFar - 1) * 24 * 60 * 60 * 1000);
    } else {
      // Full previous cycle
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const prevTimeframe = getCustomTimeframe(prevYear, prevMonth, customStartDay || 1);

      previousStart = prevTimeframe.start;
      previousEnd = prevTimeframe.end;
    }

    return {
      current: { start: currentStart, end: currentEnd },
      previous: { start: previousStart, end: previousEnd }
    };
  }, [activeYearMonth, customStartDay, useEqualDayComparison, isPartialMonthScenario, lastFetchTime]);

  const daysSoFar = useMemo(() => {
    if (!isPartialMonthScenario || !useEqualDayComparison) return undefined;
    const { current } = comparisonRanges;
    return Math.floor((current.end.getTime() - current.start.getTime()) / (24 * 60 * 60 * 1000)) + 1;
  }, [comparisonRanges, isPartialMonthScenario, useEqualDayComparison]);

  const currentMonthName = useMemo(() => {
    const { year, month } = activeYearMonth;
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }, [activeYearMonth]);

  const previousMonthName = useMemo(() => {
    const { year, month } = activeYearMonth;
    const prevDate = new Date(year, month - 2);
    return prevDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }, [activeYearMonth]);

  useEffect(() => {
    if (!isLoggedIn || !availableMonths.length) return;
    if (!selectedMonth || !availableMonths.includes(selectedMonth)) {
      setSelectedMonth(availableMonths[0]);
    }
  }, [isLoggedIn, availableMonths, selectedMonth]);

  const cardCount = analytics?.cardSpending?.length || displayData.cardSpending?.length || 0;
  const baseRows = Math.min(Math.max(cardCount || 3, 2), 6);
  const totalMonths = displayData.monthlySpend?.length || 0;

  const autoColumns = (() => {
    if (totalMonths >= baseRows * 3 && baseRows >= 4) return 3;
    if (totalMonths >= baseRows * 2) return 2;
    if (totalMonths > baseRows) return 2;
    return 1;
  })();

  const visibleMonthsCount = Math.min(
    totalMonths,
    baseRows * Math.max(autoColumns, 1) || totalMonths
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Demo Mode Banner */}
        {!hasSubscription && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-blue-900">Demo Mode</span>
                  <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-xs font-medium rounded-full">Sample Data</span>
                </div>
                <p className="text-sm text-blue-800 mb-3">
                  {isLoggedIn
                    ? 'This is sample data. Subscribe to connect your real credit cards.'
                    : 'This is sample data. Sign in to connect your real credit cards.'
                  }
                </p>
                {isLoggedIn && (
                  <Link
                    href="/subscribe"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Subscribe Now →
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Spending Analytics</h1>
              <p className="text-gray-600 mt-2">
                {isLoggedIn 
                  ? 'Detailed insights into your credit card spending patterns'
                  : 'Sign in to see your real spending analytics'
                }
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Day Picker */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Start Day
                </span>
                <div className="relative">
                  <select
                    value={customStartDay}
                    onChange={(e) => handleStartDayChange(Number(e.target.value))}
                    className="appearance-none pl-3 pr-8 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 rounded-lg font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200 hover:from-gray-100 hover:to-gray-150 cursor-pointer shadow-sm min-w-[60px]"
                    disabled={loading}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  </div>
                </div>
              </div>

              <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Viewing
              </span>
              <div className="relative">
                <select
                  id="month-picker"
                  value={monthSelectValue}
                  onChange={(e) => handleMonthChange(e.target.value)}
                  className="appearance-none pl-4 pr-10 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200 hover:from-blue-100 hover:to-indigo-100 cursor-pointer shadow-sm"
                  disabled={loading || monthOptions.length === 0}
                >
                  {monthOptions.map((month) => {
                    const [year, monthNum] = month.split('-');
                    const date = new Date(parseInt(year, 10), parseInt(monthNum, 10) - 1, 1);
                    const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    return (
                      <option key={month} value={month}>
                        {label}
                      </option>
                    );
                  })}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <ChevronDown className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-gradient-to-r from-amber-50 to-red-50 border-2 border-amber-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <Info className="h-5 w-5 text-amber-600 mt-0.5" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-amber-800 font-medium mb-3">{error}</p>
                <button
                  onClick={() => fetchAnalytics(selectedMonth || monthSelectValue, customStartDay)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        {/* iOS-style Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 flex items-center justify-center transition-opacity duration-200">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-lg px-6 py-4 flex flex-col items-center">
              {/* Simple iOS-style spinner */}
              <div className="w-6 h-6 mb-2">
                <div className="w-full h-full border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin"></div>
              </div>
              {/* Minimal text */}
              <p className="text-gray-700 text-sm font-medium">Loading...</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 p-4 md:p-6 rounded-xl shadow-sm border border-green-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start sm:items-center">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mr-3 md:mr-4 shadow-lg flex-shrink-0">
                <TrendingUp className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm font-semibold text-green-700 uppercase tracking-wider leading-tight">Total This Month</p>
                <p className="text-base md:text-xl font-bold text-gray-900 whitespace-nowrap overflow-hidden">
                  {loading ? '...' : formatCurrency(displayData.totalSpendThisMonth)}
                </p>
              </div>
            </div>
          </div>

          <div className="relative bg-gradient-to-br from-blue-50 to-cyan-50 p-4 md:p-6 rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start sm:items-center">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mr-3 md:mr-4 shadow-lg flex-shrink-0">
                <BarChart3 className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm font-semibold text-blue-700 uppercase tracking-wider leading-tight">Avg per Day</p>
                <p className="text-base md:text-xl font-bold text-gray-900 whitespace-nowrap overflow-hidden">
                  {loading ? '...' : formatCurrency(avgPerDay)}
                </p>
              </div>
            </div>
          </div>

          <div className="relative bg-gradient-to-br from-purple-50 to-pink-50 p-4 md:p-6 rounded-xl shadow-sm border border-purple-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start sm:items-center">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mr-2 md:mr-3 shadow-lg flex-shrink-0">
                <PieChart className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm font-semibold text-purple-700 uppercase tracking-wider leading-tight">Top Category</p>
                <p className={`font-bold text-gray-900 leading-tight ${
                  topCategory && topCategory.length > 12
                    ? 'text-xs md:text-sm break-words'
                    : 'text-sm md:text-lg'
                }`}
                  title={topCategory}
                >
                  {loading ? '...' : topCategory}
                </p>
              </div>
            </div>
          </div>

          <div className="relative bg-gradient-to-br from-indigo-50 to-violet-50 p-4 md:p-6 rounded-xl shadow-sm border border-indigo-100 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start sm:items-center">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mr-3 md:mr-4 shadow-lg flex-shrink-0">
                <Calendar className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm font-semibold text-indigo-700 uppercase tracking-wider leading-tight">Transactions</p>
                <p className="text-base md:text-xl font-bold text-gray-900 whitespace-nowrap overflow-hidden">
                  {loading ? '...' : displayData.transactionCount}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Spending By Card + Monthly Spending Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-8">
          {/* Spending By Card - 40% */}
          <div ref={cardSpendingSectionRef} className="lg:col-span-2 bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Spending by Card</h2>
            <div className="relative inline-flex items-center bg-gray-100 rounded-xl p-1 shadow-inner">
              <div
                className={`absolute top-1 bottom-1 bg-white rounded-lg shadow-md transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                  showCardComparison ? 'translate-x-full' : 'translate-x-0'
                }`}
                style={{
                  width: 'calc(50% - 4px)',
                  left: '4px'
                }}
              />
              <button
                onClick={() => setShowCardComparison(false)}
                className={`relative z-10 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${
                  !showCardComparison
                    ? 'text-gray-900 scale-105'
                    : 'text-gray-500 hover:text-gray-700 hover:scale-105'
                }`}
              >
                Current
              </button>
              <button
                onClick={() => setShowCardComparison(true)}
                className={`relative z-10 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${
                  showCardComparison
                    ? 'text-gray-900 scale-105'
                    : 'text-gray-500 hover:text-gray-700 hover:scale-105'
                }`}
              >
                Compare
              </button>
            </div>
          </div>
          <CardSpendingComparison
            cardSpending={displayData.cardSpending || []}
            loading={loading}
            isLoggedIn={isLoggedIn}
            showComparison={showCardComparison}
            onCardSelect={handleCardSpendingSelect}
            onTotalClick={handleTotalClick}
            isPartialMonthScenario={isPartialMonthScenario}
            useEqualDayComparison={useEqualDayComparison}
            onToggleEqualDayComparison={() => setUseEqualDayComparison(!useEqualDayComparison)}
            currentMonthName={currentMonthName}
            previousMonthName={previousMonthName}
            daysSoFar={daysSoFar}
            // Show Total Spending Trend in CardSpendingComparison when ≤4 cards
            showTotalSpendingTrend={(displayData.cardSpending || []).length <= 4}
            daysSoFar={daysSoFar}
            totalThisMonth={displayData.totalSpendThisMonth || 0}
            totalLastMonth={displayData.totalSpendLastMonth || 0}
            totalLastMonthEqualDays={displayData.totalSpendLastMonthEqualDays || 0}
          />
          </div>

          {/* Monthly Spending Chart - 60% */}
          <div className="lg:col-span-3 bg-white p-6 rounded-lg shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Monthly Spending</h2>
              {availableChartYears.length > 1 && (
                <div className="relative">
                  <select
                    value={chartSelectedYear}
                    onChange={(e) => setChartSelectedYear(e.target.value)}
                    className="appearance-none pl-4 pr-10 py-2 bg-white border border-gray-200 rounded-lg font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all duration-200 hover:border-gray-300 cursor-pointer shadow-sm text-sm"
                  >
                    {availableChartYears.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              )}
            </div>
            {loading ? (
              <p className="text-gray-500">Loading chart data...</p>
            ) : (
              <MonthlySpendingChart
                data={displayData.monthlySpend || []}
                visibleCount={Math.min(12, totalMonths || 12)}
                selectedYear={chartSelectedYear}
                availableYears={availableChartYears}
                onYearChange={setChartSelectedYear}
                totalThisMonth={displayData.totalSpendThisMonth || 0}
                totalLastMonth={displayData.totalSpendLastMonth || 0}
                totalLastMonthEqualDays={displayData.totalSpendLastMonthEqualDays || 0}
                isPartialMonthScenario={isPartialMonthScenario}
                useEqualDayComparison={useEqualDayComparison}
                onToggleEqualDayComparison={() => setUseEqualDayComparison(!useEqualDayComparison)}
                onTotalClick={handleTotalClick}
                currentMonthName={currentMonthName}
                previousMonthName={previousMonthName}
                daysSoFar={daysSoFar}
                showTotalSpendingTrend={(displayData.cardSpending || []).length > 4}
              />
            )}
          </div>
        </div>

        {/* Category Breakdown + Monthly Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Category Breakdown</h2>
            {loading ? (
              <p className="text-gray-500">Loading category data...</p>
            ) : displayData.categories?.length > 0 ? (
              <CategoryBreakdown categories={displayData.categories} />
            ) : isLoggedIn ? (
              <p className="text-gray-500 text-center">No transaction data found. Connect a credit card to get started.</p>
            ) : (
              <CategoryBreakdown categories={displayData.categories} />
            )}
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Monthly Comparison</h2>
            {loading ? (
              <p className="text-gray-500">Loading comparison data...</p>
            ) : (
              <MonthlyComparison 
                comparisons={displayData.monthlyComparison} 
                isLoggedIn={isLoggedIn} 
              />
            )}
          </div>
        </div>

        {/* APR Cost Calculator - Expandable Section */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-sm">
            <button
              onClick={() => setIsAPRCalculatorOpen(!isAPRCalculatorOpen)}
              className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center">
                <Calculator className="h-6 w-6 text-indigo-600 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">APR Cost Calculator</h2>
              </div>
              {isAPRCalculatorOpen ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {isAPRCalculatorOpen && (
              <div className="px-6 pb-6 border-t">
                <div className="pt-6">
                  <APRCalculator />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <TransactionCatalogModal
        isOpen={Boolean(transactionModalContext)}
        context={transactionModalContext}
        onClose={() => setTransactionModalContext(null)}
      />
    </div>
  );
}
